import {json, type LoaderArgs, redirect} from '@shopify/remix-oxygen';
import type {Page as PageType} from '@shopify/hydrogen/storefront-api-types';
import {useLoaderData, useLocation, useParams} from '@remix-run/react';
import invariant from 'tiny-invariant';
import {PageHeader} from '~/components';
import {CACHE_LONG, routeHeaders} from '~/data/cache';
import {canonicalUrl, seoPayload} from '~/lib/seo.server';
import type {LinksFunction} from '@shopify/remix-oxygen';

//domains
import {usePageQuery as fetchPageData} from 'domains/page';
import {getLangagueCodeForSanityQuery} from '~/domains/sanity';

//components
import PageComponent from 'components/page';
import {Link} from '~/components';
import Container from 'components/Container';
import Brand from 'components/page/Brand';
import BrandStory from 'components/page/BrandStory';

//translation
import translation from 'translations/global.json';
import {routeLocale} from 'domains/global';
import {legacyRedirectTarget} from '~/lib/legacyRedirects';
import {fetchInStockVendors} from 'domains/nativeCollection';

export const headers = routeHeaders;

export const links: LinksFunction = () => {
  return [
    {
      rel: 'stylesheet',
      href: 'https://cdn.jsdelivr.net/npm/swiper@8/swiper-bundle.min.css',
    },
  ];
};

export async function loader({request, params, context}: LoaderArgs) {
  invariant(params.pageHandle, 'Missing page handle');

  const {pageData, allBrands, brandsSettings} = await fetchPageData({
    languageCode: getLangagueCodeForSanityQuery(
      context.storefront.i18n.language,
    ),
    pageSlug: params.pageHandle,
  });

  // const {page} = await context.storefront.query<{page: PageType}>(PAGE_QUERY, {
  //   variables: {
  //     handle: params.pageHandle,
  //     language: context.storefront.i18n.language,
  //   },
  // });

  // if (!page) {
  //   throw new Response(null, {status: 404});
  // }

  // Unknown page slug: render the 404 boundary. Reading pageData.title
  // unguarded here used to turn every unknown /pages/* URL into a 500.
  if (!pageData) {
    const legacy = legacyRedirectTarget(new URL(request.url).pathname);
    if (legacy) {
      throw redirect(legacy, 301);
    }
    throw new Response(null, {status: 404});
  }

  const url = canonicalUrl(request.url);
  const parsedUrl = new URL(url);
  const pathPrefix = context.storefront.i18n.pathPrefix.replace(/\/$/, '');
  const homeUrl = `${parsedUrl.origin}${pathPrefix}/`;
  const isEnglish = context.storefront.i18n.language === 'EN';
  const pageTitle =
    pageData.title ||
    (params.pageHandle === 'brands'
      ? isEnglish
        ? 'All Brands'
        : '所有品牌'
      : params.pageHandle === 'about-us'
      ? isEnglish
        ? 'About Us'
        : '關於我們'
      : pageData.title);
  const title = pageData.seoSanity?.title || pageTitle;
  // This CMS flag is projected independently of the requested locale, so
  // both pages agree on whether an English alternate actually exists.
  // These two pages render bilingual content supplied by their components.
  const hasEnglishTranslation =
    params.pageHandle === 'brands' ||
    params.pageHandle === 'about-us' ||
    pageData.hasEnglishTranslation === true;
  const description =
    pageData.seoSanity?.description ||
    (params.pageHandle === 'brands' ? brandsSettings?.description : undefined);
  const image = pageData.seoSanity?.image;
  const seo = {
    description,
    title,
    ...(image?.url
      ? {
          media: {
            type: 'image' as const,
            url: image.url,
            width: image.width,
            height: image.height,
            altText: image.altText,
          },
        }
      : {}),
    url,
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        '@id': `${url}#webpage`,
        name: title,
        ...(description ? {description} : {}),
        ...(image?.url ? {image: image.url} : {}),
        url,
        inLanguage: isEnglish ? 'en-HK' : 'zh-Hant-HK',
        isPartOf: {'@id': `${parsedUrl.origin}/#website`},
        breadcrumb: {'@id': `${url}#breadcrumb`},
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        '@id': `${url}#breadcrumb`,
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: isEnglish ? 'Home' : '主頁',
            item: homeUrl,
          },
          {'@type': 'ListItem', position: 2, name: pageTitle},
        ],
      },
    ],
  };

  const selectedLocale =
    context.storefront.i18n.language.toLowerCase() as routeLocale;

  // The brand directory follows Shopify live: every vendor with in-stock
  // product, not Sanity's hand-maintained list. Sanity still supplies the
  // banner, copy and the featured-logos strip, and stays the fallback if
  // the scan fails so the page never renders empty.
  const vendorBrands =
    params.pageHandle === 'brands'
      ? await fetchInStockVendors(context.storefront).catch(() => undefined)
      : undefined;

  return json(
    {
      // page,
      seo,
      title: pageTitle,
      hasEnglishTranslation,
      // About Us renders the hardcoded BrandStory timeline, so don't ship its
      // (large, now-unused) page_sections blob to the client.
      modules: params.pageHandle === 'about-us' ? undefined : pageData?.modules,
      allBrands,
      vendorBrands,
      brandsSettings,
      translation: translation[selectedLocale],
    },
    {
      headers: {
        'Cache-Control': CACHE_LONG,
      },
    },
  );
}

export default function Page() {
  const {title, translation} = useLoaderData<typeof loader>();
  const {pageHandle} = useParams();
  // Brand links and directory copy must also be present before hydration.
  if (pageHandle === 'brands') {
    return <Brand />;
  }
  return (
    <>
      <Container>
        <div className="">
          <nav className="mt-6 mb-8 flex flex-wrap items-center text-sm text-slate-500">
            <Link
              to="/"
              className="transition-colors duration-200 ease-out hover:text-hk-orange"
            >
              {translation?.home ?? 'Home'}
            </Link>
            <span className="mx-2 inline-block">/</span>
            <span className="">{title}</span>
          </nav>
          <header className="mb-10 text-center">
            <h1 className="text-xl font-bold uppercase tracking-wide lg:text-[28px] lg:leading-[40px]">
              {title}
            </h1>
            <span className="mt-3 inline-block h-[3px] w-12 rounded-full bg-hk-orange"></span>
          </header>
          {/* About Us renders the brand-story timeline instead of the old
              repetitive business-list text blocks. */}
          {pageHandle === 'about-us' ? <BrandStory /> : <PageComponent />}
        </div>
      </Container>
    </>
  );
}

const PAGE_QUERY = `#graphql
  query PageDetails($language: LanguageCode, $handle: String!)
  @inContext(language: $language) {
    page(handle: $handle) {
      id
      title
      body
      seo {
        description
        title
      }
    }
  }
`;
